import type { Breadcrumb } from '@sentry/vue'
import { redactSentryUrl } from './redactApiPath'

// `beforeBreadcrumb` hook for Sentry.init (wired in main.ts).
//
// Sentry records a breadcrumb for every fetch and XHR the app makes — and for
// every in-app navigation — and the breadcrumb carries the RAW URL. On
// health-adjacent routes that URL is the payload:
// /api/admin/bookings/{uuid}/journal says a named booking has a treatment
// journal, /api/admin/notes/search?q=… carries free text the operator typed
// about a person. The same breadcrumbs also carry credential
// path segments — a customer's booking magic token, an invitee token — since
// `redactApiPath` is otherwise only called manually at explicit Sentry
// capture sites in apiClient.ts, never on the SDK's own fetch/XHR
// breadcrumbs. The existing `beforeSend` scrubbing works on breadcrumb data
// KEY NAMES (password|totp|token|csrf|secret) and never looks at the value
// of `url`, so today both classes of secret ship intact.
//
// This runs at breadcrumb-record time — before the string is ever attached to
// an event — so the value never reaches the transport for errors, feedback or
// replays alike.
//
// The breadcrumb is redacted, not dropped: "the operator was on a notes screen
// when this broke" is real triage signal, and losing it would push us back to
// guessing from stack traces.
//
// Lives in its own module (rather than inline in main.ts) so it is importable
// from a unit test — main.ts mounts the app on import.

// Which fields to redact on each category the SDK records a URL on.
//
// `fetch` / `xhr`: the request URL, on `data.url`. Which of the two the SDK
// uses depends on whether the call site went through fetch or XMLHttpRequest,
// so missing one would leak on exactly the surfaces that use the other.
//
// `navigation`: the PAGE url, on `data.from` and `data.to`, recorded on every
// history push. Stemne puts credentials in page URLs too —
// `/videorom?url=<whereby room>&token=<booking magic token>` (see
// ROOM_URL_ROUTE in redactApiPath.ts) — and this hook used to return early on
// any category other than fetch/xhr, so a customer joining a meeting stamped
// both values onto a breadcrumb that rode along with every event for the rest
// of the session.
//
// A Map, not an object literal: `category` is an arbitrary string off the
// wire, and a plain-object lookup for 'constructor' or 'toString' would return
// something truthy off the prototype and then throw when we tried to iterate
// it. Throwing inside beforeBreadcrumb would cost the crumb, not just the
// redaction.
const URL_FIELDS = new Map<string, readonly string[]>([
  ['fetch', ['url']],
  ['xhr', ['url']],
  ['navigation', ['from', 'to']],
])

export function redactHealthBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const fields = URL_FIELDS.get(breadcrumb.category ?? '')
  if (!fields) return breadcrumb

  const data = breadcrumb.data
  if (data) {
    for (const field of fields) {
      if (typeof data[field] === 'string') {
        data[field] = redactSentryUrl(data[field])
      }
    }
  }

  // Some SDK paths put a human-readable "GET <url> [200]" line on `message`.
  // Redact per whitespace-delimited token so the method and status survive.
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = breadcrumb.message.replace(/\S+/g, (token) => redactSentryUrl(token))
  }

  return breadcrumb
}
